import { Component } from '@opten/compiler/metadata/component';

declare const Cookiebot: any;

let scriptPromise: Promise<void> | null = null;

function loadSpinnerScript(src: string): Promise<void> {
	if (scriptPromise !== null) {
		return scriptPromise;
	}

	scriptPromise = new Promise<void>((resolve, reject) => {
		const existing = document.querySelector(
			`script[data-photo-motion-spinner="true"]`
		);
		if (existing) {
			resolve();
			return;
		}

		const script = document.createElement('script');
		script.type = 'module';
		script.src = src;
		script.setAttribute('data-photo-motion-spinner', 'true');
		script.addEventListener('load', () => resolve());
		script.addEventListener('error', () => reject());
		document.body.appendChild(script);
	});

	return scriptPromise;
}

function hasStatisticsConsent(): boolean {
	if (typeof Cookiebot === 'undefined') {
		return true;
	}
	return Cookiebot.consent && Cookiebot.consent.statistics === true;
}

/**
 * Loads the Photo-Motion spinner web component script, but only once statistics
 * cookies have been accepted.
 *
 * The same spinner is rendered twice: once in the inline gallery and once in the
 * lightbox. The viewer measures its container while it is being upgraded, so the
 * copy inside the closed lightbox would come up at 0x0 and stay broken after the
 * modal opens. To avoid that, the element is kept out of the document until its
 * container actually has a box, and a fresh one is mounted every time.
 */
@Component({ className: 'js-gallery-360-spinner', observe: true })
export class Gallery360SpinnerComponent {
	private src: string;
	private template: HTMLElement;
	private anchor: Comment;
	private mounted: HTMLElement = null;
	private observer: IntersectionObserver = null;

	constructor(private element: HTMLElement) {
		this.src = this.element.getAttribute('data-spinner-script');

		const spinner = this.element.querySelector(
			'photo-motion-spinner'
		) as HTMLElement;

		if (!this.src || !spinner) {
			return;
		}

		// Detach before the script has a chance to upgrade it. Runs synchronously
		// during component init, so the element is still pristine at this point.
		this.template = spinner.cloneNode(true) as HTMLElement;
		this.anchor = document.createComment('photo-motion-spinner');
		spinner.parentNode.replaceChild(this.anchor, spinner);

		this.load();
		window.addEventListener('CookiebotOnAccept', this.load);
	}

	private load = () => {
		if (!hasStatisticsConsent()) {
			return;
		}

		loadSpinnerScript(this.src).then(
			() => {
				this.element.classList.add('is-loaded');
				this.watch();
			},
			() => this.element.classList.add('has-error')
		);
	};

	/**
	 * Mounts the spinner as soon as its slide is on screen and drops it again
	 * once the lightbox is closed, so the next open starts from a clean element.
	 */
	private watch() {
		if (this.observer !== null) {
			return;
		}

		if (typeof IntersectionObserver === 'undefined') {
			this.mount();
			return;
		}

		this.observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					this.mount();
				} else if (this.element.offsetParent === null) {
					// hidden by an ancestor - the lightbox was closed
					this.unmount();
				}
			});
		});
		this.observer.observe(this.element);
	}

	private mount() {
		if (this.mounted !== null || !this.anchor.parentNode) {
			return;
		}

		this.mounted = this.template.cloneNode(true) as HTMLElement;
		this.anchor.parentNode.insertBefore(
			this.mounted,
			this.anchor.nextSibling
		);

		// let the viewer and the surrounding swiper pick up the final size
		requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
	}

	private unmount() {
		if (this.mounted === null) {
			return;
		}

		if (this.mounted.parentNode) {
			this.mounted.parentNode.removeChild(this.mounted);
		}
		this.mounted = null;
	}

	destroy() {
		window.removeEventListener('CookiebotOnAccept', this.load);
		if (this.observer !== null) {
			this.observer.disconnect();
			this.observer = null;
		}
		this.unmount();
	}
}
